import { AlertTriangle } from '@untitledui/icons';
import { useEffect, useState } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { StackingLayer, useStackingZIndex } from '@/contexts/StackingContext';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays/alerts';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { cx } from '@/utils/cx';

import { useOrg } from '../../contexts/OrgContext';

interface ProfileData {
  user_has_valid_totp_device: boolean;
}

export default function TOTPManage() {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Modal state
  const zIndex = useStackingZIndex();
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isReset, setIsReset] = useState(false);

  useEffect(() => {
    loadProfileData();
  }, []);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      const response = await fetchWithDefaults('/users/api/profile/data/');

      if (response.ok) {
        const data = await response.json();
        setProfileData(data);
      } else {
        setError("We couldn't load your profile. Try again.");
      }
    } catch (err) {
      console.error('Error loading profile data:', err);
      setError("We couldn't load your profile. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async (resetMode = false) => {
    setIsReset(resetMode);
    setShowDisableConfirm(true);
  };

  const confirmDisable2FA = async () => {
    setShowDisableConfirm(false);

    try {
      setActionLoading(true);
      setError(null);

      const response = await fetchWithDefaults('/users/api/totp-disable/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        if (isReset) {
          setError(null);
          // Reload profile data to refresh state
          await loadProfileData();
          setActionLoading(false);
          toast.success(
            '2FA state has been reset. You can now try enabling 2FA again.'
          );
        } else {
          navigate(`/${currentOrg?.id}/profile`, {
            state: { message: 'Two-factor authentication has been disabled.' },
          });
        }
      } else {
        const errorData = await response.json();
        setError(
          errorData.error ||
            `We couldn't ${isReset ? 'reset' : 'disable'} two-factor authentication. Try again.`
        );
      }
    } catch (err) {
      console.error('Error with 2FA operation:', err);
      setError(
        `We couldn't ${isReset ? 'reset' : 'disable'} two-factor authentication. Try again.`
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center min-h-full'>
        <LoadingIndicator size='lg' />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className='flex justify-center items-center min-h-full p-4'>
        <AlertFloating
          color='error'
          title='No profile data available'
          description='Please try refreshing the page.'
        />
      </div>
    );
  }

  const hasTOTP = profileData.user_has_valid_totp_device;

  return (
    <div className='min-h-full bg-bg-primary p-4'>
      <div className='container mx-auto max-w-2xl space-y-6'>
        <div className='bg-bg-primary rounded-xl border border-border-secondary shadow-sm'>
          <div className='px-6 py-5 border-b border-border-secondary'>
            <div className='flex items-center justify-between'>
              <h2 className='text-lg font-semibold text-secondary'>
                Two-factor authentication
              </h2>
              <Button
                onClick={() => navigate(`/${currentOrg?.id}/profile`)}
                color='tertiary'
                size='sm'
              >
                ← Back to profile
              </Button>
            </div>
          </div>

          <div className='p-6'>
            {error && (
              <AlertFloating
                color='error'
                title={error}
                description=''
                onClose={() => setError(null)}
                dismissLabel='Dismiss'
              />
            )}

            {hasTOTP ? (
              <div className='space-y-4'>
                <div className='flex items-center gap-3'>
                  <Badge type='color' size='sm' color='success'>
                    Enabled
                  </Badge>
                  <span className='text-primary'>Authenticator app</span>
                </div>

                <div className='flex gap-2'>
                  <Button
                    onClick={() => handleDisable2FA(false)}
                    isDisabled={actionLoading}
                    color='primary-destructive'
                    size='sm'
                    isLoading={actionLoading}
                  >
                    Disable 2FA
                  </Button>
                </div>
              </div>
            ) : (
              <div className='space-y-4'>
                <p className='text-tertiary'>
                  Two-factor authentication is not enabled on your account.
                </p>
                <div className='flex gap-2'>
                  <Button
                    onClick={() => {
                      logger.debug('Navigate to TOTP setup clicked');
                      navigate('/account/authenticators/totp');
                    }}
                    color='primary'
                    size='md'
                  >
                    Enable 2FA
                  </Button>

                  <Button
                    onClick={() => handleDisable2FA(true)}
                    isDisabled={actionLoading}
                    color='secondary'
                    size='sm'
                    isLoading={actionLoading}
                  >
                    Reset 2FA state
                  </Button>
                </div>

                <AlertFloating
                  color='gray'
                  title='Troubleshooting'
                  description="If you're having issues enabling 2FA, try clicking 'Reset 2FA State' first to clear any inconsistent configuration."
                />
              </div>
            )}
          </div>
        </div>

        {/* Disable/Reset 2FA confirmation modal */}
        <ModalOverlay
          isOpen={showDisableConfirm}
          onOpenChange={open => setShowDisableConfirm(open)}
          isDismissable
          className={cx(
            'fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm',
            !prefersReducedMotion && 'animate-in fade-in duration-200'
          )}
          style={{ zIndex }}
        >
          <Modal
            className={cx(
              'relative max-w-sm w-full mx-4 bg-bg-primary rounded-2xl shadow-2xl',
              !prefersReducedMotion && 'animate-in zoom-in-95 duration-200'
            )}
            style={{ zIndex: zIndex + 10 }}
          >
            <Dialog className='outline-none p-6'>
              <StackingLayer>
                <div className='flex items-start gap-3 mb-4'>
                  <div className='w-10 h-10 rounded-full bg-error-50 flex items-center justify-center flex-shrink-0'>
                    <AlertTriangle className='w-5 h-5 text-error-600' />
                  </div>
                  <div>
                    <h3 className='text-lg font-semibold text-primary mb-2'>
                      {isReset
                        ? 'Reset 2FA state?'
                        : 'Disable two-factor authentication?'}
                    </h3>
                    <p className='text-secondary text-sm'>
                      {isReset
                        ? 'This will clear any inconsistent configuration.'
                        : 'This will make your account less secure. Are you sure you want to continue?'}
                    </p>
                  </div>
                </div>
                <div className='flex gap-3'>
                  <Button
                    color='secondary'
                    size='md'
                    className='flex-1'
                    onClick={() => setShowDisableConfirm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color='primary-destructive'
                    size='md'
                    className='flex-1'
                    onClick={confirmDisable2FA}
                    isLoading={actionLoading}
                  >
                    {isReset ? 'Reset' : 'Disable'}
                  </Button>
                </div>
              </StackingLayer>
            </Dialog>
          </Modal>
        </ModalOverlay>
      </div>
    </div>
  );
}
